import React, { useState, useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import { format } from 'date-fns';
import { useClickOutside } from '../../../../hooks/useClickOutside';
import { supabase } from '../../../../lib/supabase';
import { supabaseUnderLeaseService } from '../../../../services/supabase/underleases';
import { supabaseLeaseService } from '../../../../services/supabase/leases';
import { supabaseLeaseLotService } from '../../../../services/supabase/lease-lots';

interface AddUnderLeaseModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  preventCloseOnOutsideClick?: boolean;
}

// Define the form data interface
interface FormData {
  underlease_lease: string;
  underlease_holder: string;
  underlease_start: string;
  underlease_end: string;
  underlease_use: string;
}

export default function AddUnderLeaseModal({
  isOpen,
  onClose,
  onSuccess,
  preventCloseOnOutsideClick = true
}: AddUnderLeaseModalProps) {
  const modalContentRef = useRef<HTMLDivElement>(null);
  useClickOutside(modalContentRef, onClose, preventCloseOnOutsideClick);
  // Initialize form with sensible defaults
  const getInitialFormData = (): FormData => ({
    underlease_lease: '',
    underlease_holder: '',
    underlease_start: format(new Date(), 'yyyy-MM-dd'),
    underlease_end: format(new Date(new Date().setFullYear(new Date().getFullYear() + 1)), 'yyyy-MM-dd'),
    underlease_use: ''
  });

  // State management
  const [formData, setFormData] = useState<FormData>(getInitialFormData());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // State for dropdown options
  const [leases, setLeases] = useState<{ id: number, name: string }[]>([]);
  const [leaseholders, setLeaseholders] = useState<{ id: number, name: string }[]>([]);

  // Reset form state
  const resetForm = () => {
    setFormData(getInitialFormData());
    setError(null);
    setIsSubmitting(false);
  };

  // Wrap the original onClose to reset form state
  const handleClose = () => {
    resetForm();
    onClose();
  };

  // Fetch leases and leaseholders for dropdowns
  useEffect(() => {
    if (isOpen) {
      // Fetch subleases
      const fetchSubleases = async () => {
        try {
          // Use getAllEnhanced which reliably fetches leaseholders safely
          const subleases = await supabaseLeaseService.getAllEnhanced();

          if (!subleases || subleases.length === 0) {
            setLeases([]);
            return;
          }

          // Get lots for these subleases using robust service
          const leaseIds = subleases.map(s => s.lease_id);
          const lotsData = await supabaseLeaseLotService.getLotsForMultipleLeaseIds(leaseIds);

          setLeases(subleases.map(item => {
            const lots = lotsData[item.lease_id] || [];
            const primaryLot = lots.length > 0 ? lots[0] : null;

            const comm = primaryLot?.community_name || primaryLot?.lot_community || 'Unknown';
            const lot = primaryLot?.lot_id || 'Unknown';
            const holderName = item.leaseholder_name;
            const holder = holderName ?
              (holderName.length > 15 ? holderName.substring(0, 15) + '...' : holderName)
              : 'Unknown';
            const year = item.lease_end ? new Date(item.lease_end).getFullYear() : 'N/A';

            return {
              id: item.lease_id,
              name: `${comm} - ${lot} - ${holder} / ${year}`
            };
          }));
        } catch (err) {
          console.error('Error fetching subleases:', err);
          setError('Failed to load subleases');
        }
      };

      // Fetch leaseholders
      const fetchLeaseholders = async () => {
        try {
          const { data, error } = await supabase
            .from('leaseholders')
            .select('holder_id, holder_name')
            .order('holder_name', { ascending: true });

          if (error) throw error;

          setLeaseholders((data as any[] || []).map(item => ({
            id: item.holder_id,
            name: item.holder_name
          })));
        } catch (err) {
          console.error('Error fetching leaseholders:', err);
          setError('Failed to load leaseholders');
        }
      };

      fetchSubleases();
      fetchLeaseholders();
    }
  }, [isOpen]);

  // Handle form input changes
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    // Create a timeout promise to prevent hanging
    const timeoutPromise = new Promise((_, reject) => {
      setTimeout(() => reject(new Error('Request timed out after 10 seconds')), 10000);
    });

    try {
      // Process form data
      const processedData = {
        underlease_lease: parseInt(formData.underlease_lease),
        underlease_holder: parseInt(formData.underlease_holder),
        underlease_start: formData.underlease_start,
        underlease_end: formData.underlease_end,
        underlease_use: formData.underlease_use
      };

      console.log('Creating under-lease with data:', processedData);

      // Submit data
      await Promise.race([
        supabaseUnderLeaseService.create(processedData),
        timeoutPromise
      ]);

      // Reset form and close modal
      resetForm();
      onSuccess();
      onClose();
    } catch (err) {
      console.error('Error adding under-lease:', err);
      setError(`Failed to add under-lease: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div ref={modalContentRef} className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-800">Add New Under-Lease</h2>
            <button
              onClick={handleClose}
              className="text-gray-500 hover:text-gray-700"
              aria-label="Close"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-md">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Sublease ID */}
              <div>
                <label htmlFor="underlease_lease" className="block text-sm font-medium text-gray-700 mb-1">
                  Sublease ID
                </label>
                <select
                  id="underlease_lease"
                  name="underlease_lease"
                  value={formData.underlease_lease}
                  onChange={handleChange}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
                  required
                >
                  <option value="">Select a lease</option>
                  {leases.map(lease => (
                    <option key={lease.id} value={lease.id}>
                      {lease.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Leaseholder */}
              <div>
                <label htmlFor="underlease_holder" className="block text-sm font-medium text-gray-700 mb-1">
                  Underlease-holder
                </label>
                <select
                  id="underlease_holder"
                  name="underlease_holder"
                  value={formData.underlease_holder}
                  onChange={handleChange}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
                  required
                >
                  <option value="">Select a leaseholder</option>
                  {leaseholders.map(leaseholder => (
                    <option key={leaseholder.id} value={leaseholder.id}>
                      {leaseholder.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Lease Start */}
              <div>
                <label htmlFor="underlease_start" className="block text-sm font-medium text-gray-700 mb-1">
                  Start Date
                </label>
                <input
                  type="date"
                  id="underlease_start"
                  name="underlease_start"
                  value={formData.underlease_start}
                  onChange={handleChange}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
                  required
                />
              </div>

              {/* Lease End */}
              <div>
                <label htmlFor="underlease_end" className="block text-sm font-medium text-gray-700 mb-1">
                  End Date
                </label>
                <input
                  type="date"
                  id="underlease_end"
                  name="underlease_end"
                  value={formData.underlease_end}
                  onChange={handleChange}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
                  required
                />
              </div>
            </div>

            {/* Use Type */}
            <div>
              <label htmlFor="underlease_use" className="block text-sm font-medium text-gray-700 mb-1">
                Use Type
              </label>
              <textarea
                id="underlease_use"
                name="underlease_use"
                value={formData.underlease_use}
                onChange={handleChange}
                rows={3}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
                required
              />
            </div>

            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={handleClose}
                className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
              >
                {isSubmitting ? 'Adding...' : 'Add Under-Lease'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
